/**
 * はじめて用の URL に載せるアクセスコード（MYP-45・2026-09-29 岩崎さん了承）。
 *
 * お客様は LINE で届いた「はじめて用のURL」を押すだけで、登録の画面がアクセスコード入力済みで開く。
 * 担当者の画面（URL を組む側）と、お客様の登録画面（読む側）が同じ決まりを使うように、ここ1か所に置く。
 *
 * ## 🔴 コードは「#」の後ろ（フラグメント）に載せる
 *
 * ブラウザは「#」から先をサーバーへ送らない。だからコードは
 * サーバーのアクセスログ・LINE のリンクプレビュー（LINE のサーバーが URL を取りに来る）・
 * 次のページへの Referer のどれにも載らない。パスや `?code=` に載せるとこれらに残る。
 * 読んだ画面はすぐにアドレス欄から「#」を消す（`InviteAcceptInner` 側）。
 *
 * 🔴 ここではコードの形を確かめない。区切り・大文字小文字・全角を揃えて照合するのはサーバー
 *    （`access-code.ts` の `normalizeAccessCode`）の仕事で、規則を2か所に持たない。
 *    読む側は長さだけを見る（長い文字を入力欄やサーバーへ流さない）。
 *
 * 担当者の画面・お客様の画面・`instrumentation-client.ts` の3か所から読むため `server-only` を付けない。
 * アドレス欄を触る関数（下）は window を引数で受け取る（呼んだときだけ動く・読み込んだだけでは何もしない）。
 */

/** 「#」の後ろでアクセスコードを表す名前（`#code=XXXX-XXXX-XXXX`）。 */
export const ACCESS_CODE_FRAGMENT_KEY = 'code'

/**
 * 読み取る値の長さの上限。アクセスコードは区切り込みで14文字（`XXXX-XXXX-XXXX`）なので、
 * 空白や全角が混ざっても十分に収まる長さにしてある。受け入れの口の段階0（確認だけ）も同じ上限で断る。
 */
export const ACCESS_CODE_FRAGMENT_MAX_LENGTH = 64

/**
 * URL の「#」の後ろにアクセスコードを載せる（例 `…/customer-portal/invite?openExternalBrowser=1#code=AAAA-BBBB-CCCC`）。
 * URL として読めない値はそのまま返す（壊した URL を渡さない）。
 */
export function withAccessCodeFragment(url: string, accessCode: string): string {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch (_notAUrl) {
    // 読めない値はコードを載せずにそのまま返す（壊した URL を渡さない）。
    return url
  }
  parsed.hash = new URLSearchParams({ [ACCESS_CODE_FRAGMENT_KEY]: accessCode }).toString()
  return parsed.toString()
}

/**
 * `location.hash`（`#code=…`）からアクセスコードを読む。無い・空・長すぎるときは null。
 */
export function readAccessCodeFromFragment(hash: string): string | null {
  if (!hash) return null
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const value = params.get(ACCESS_CODE_FRAGMENT_KEY)?.trim()
  if (!value || value.length > ACCESS_CODE_FRAGMENT_MAX_LENGTH) return null
  return value
}

/**
 * 文字列の中の「#」から先（空白か次の「#」で終わる）。URL にも自由文（例外文）にも当てる。
 * 🔴 引用符・山かっこでは区切らない。ブラウザの URL の「#」の中には `'` `"` `<` `>` がそのまま残せるので、
 *    そこで区切ると `#foo='&code=A` の A を取りこぼす（Codex SO 2026-09-29 2周目 HIGH）。
 * 🔴 次の「#」では区切る。区切らないと、JSON のように空白なしで並んだ前の URL の「#」が後ろの URL まで飲み込み、
 *    後ろの `#code=` を1つのキーとして読めなくなる（Codex SO 2026-09-29 3周目 MEDIUM）。
 */
const FRAGMENT_IN_TEXT = /#[^\s#]*/g

/**
 * 文字列（URL・例外文・breadcrumb など）に含まれる「#」の後ろのうち、アクセスコードを持つものを
 * `#[redacted]` に置き換える（障害監視へ送る前に使う・`lib/sentry/pii.ts`）。
 *
 * 🔴 読む側（`readAccessCodeFromFragment`）と同じ読み方（URLSearchParams）で「コードを持つか」を決める。
 *    正規表現で `code=` を探すと、`#code=A&code=x`（画面は最初の A を読む）や `#%63ode=A`（キーのエンコード）を
 *    取りこぼす（Codex SO 2026-09-29 MEDIUM）。コードを持つ「#」は丸ごと伏せる（ほかの値は障害調査に要らない）。
 */
export function redactAccessCodeFragments(value: string): string {
  return value.replace(FRAGMENT_IN_TEXT, (fragment) =>
    new URLSearchParams(fragment.slice(1)).has(ACCESS_CODE_FRAGMENT_KEY) ? '#[redacted]' : fragment
  )
}

// ─── アドレス欄から取り出す（Sentry が URL を読む前に・Codex SO 2026-09-29 2周目 HIGH） ───────────────

/** 取り出したアクセスコードを画面へ渡すまで預ける、window の上の置き場の名前。 */
const TAKEN_CODE_PROPERTY = '__gforceAccessCodeFromUrl'

/** アドレス欄からアクセスコードを取り出したときに window へ流す知らせ（開いたままの画面が受け取り直す）。 */
export const ACCESS_CODE_TAKEN_EVENT = 'gforce:access-code-taken'

/** はじめて用の URL で開く画面（登録の画面）。 */
const INVITE_PATH = '/customer-portal/invite'

/**
 * 登録の画面（`/customer-portal/invite` と旧い入口 `/customer-portal/invite/<token>`）か。
 * ここ以外の「#」は触らない（ほかの機能の「#」を消さない）。Session Replay もここでは録らない（`lib/sentry/replay-gate.ts`）。
 */
export function isAccessCodePagePath(pathname: string): boolean {
  return pathname === INVITE_PATH || pathname.startsWith(`${INVITE_PATH}/`)
}

/**
 * 登録の画面のアドレス欄の `#code=` を消し、読めたコードを返す（預けない・知らせない）。
 * 消すものが無ければ undefined、code を持つが読めない（長すぎる）なら null（それでも「#」は消す）。
 */
function stripAccessCodeFromAddressBar(win: Window): string | null | undefined {
  const { hash, pathname, search } = win.location
  if (!isAccessCodePagePath(pathname)) return undefined
  if (!new URLSearchParams(hash.slice(1)).has(ACCESS_CODE_FRAGMENT_KEY)) return undefined
  const accessCode = readAccessCodeFromFragment(hash)
  // 🔴 素の replaceState で消す（Codex SO 2026-09-29 3周目 HIGH）。Sentry・Next.js は history.replaceState を
  //    差し替えて包み、Sentry の包みは本物が走る前に Replay を再開させて、消す前の location.href を録る。
  //    包みは history の上に置かれるので、History.prototype のものが素のまま残っている。
  History.prototype.replaceState.call(win.history, win.history.state, '', `${pathname}${search}`)
  return accessCode
}

/**
 * 登録の画面のアドレス欄に `#code=` があれば、「#」から先を消し、コードを window に預けて知らせる。
 *
 * 🔴 `instrumentation-client.ts` が `Sentry.init` より前に呼ぶ。Sentry（ページの URL・遷移の記録・Session Replay の
 *    録画の初めの href）が URL を読む前にコードを消すのが本体で、伏せ字（`redactAccessCodeFragments`）と
 *    Replay を始めない判定（`lib/sentry/replay-gate.ts`）は二重の守り。
 * 🔴 同じ画面のまま「#」だけ変わる移動（アドレス欄に貼り直す）にも、`popstate`・`hashchange` で呼ぶ。
 *    受け手は登録した順に走るので、Sentry より前に登録すれば Sentry が読む前に消える
 *    （`@sentry/browser-utils` の history 計測は `addEventListener('popstate')` で `location.href` を読む）。
 * 読めない（長すぎる）コードでも、code を持つ「#」は消す（画面へは渡さない）。
 */
export function moveAccessCodeOutOfAddressBar(win: Window): void {
  const accessCode = stripAccessCodeFromAddressBar(win)
  if (!accessCode) return
  Reflect.set(win, TAKEN_CODE_PROPERTY, accessCode)
  win.dispatchEvent(new Event(ACCESS_CODE_TAKEN_EVENT))
}

/**
 * 登録の画面が、アドレス欄から取り出されたアクセスコードを受け取る（1回きり・受け取ったら置き場を空にする）。
 * 預けられていなければ（`instrumentation-client.ts` が走らなかったとき）、自分でアドレス欄から取り出す
 * （このときは知らせを流さない。流すと同じ画面の受け手が先に受け取り、ここが空を返す）。
 */
export function takeAccessCodeForPage(win: Window): string | null {
  if (!Reflect.has(win, TAKEN_CODE_PROPERTY)) return stripAccessCodeFromAddressBar(win) ?? null
  const taken: unknown = Reflect.get(win, TAKEN_CODE_PROPERTY)
  Reflect.deleteProperty(win, TAKEN_CODE_PROPERTY)
  return typeof taken === 'string' ? taken : null
}
